import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock } from "lucide-react";

export default function NetworkSection({ decision, updateDecision, team }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="card-header-flight-deck">
          <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" /> {t("decisions.scheduleOptimization")}</CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-5">
          <div>
            <Label className="text-xs">{t("decisions.scheduleOptimization")}</Label>
            <div className="space-y-2 mt-2">
              {[
                { value: "peak_only", label: "Peak Only", desc: "Higher yield per flight, lower utilization" },
                { value: "balanced", label: "Balanced", desc: "Standard scheduling across the day" },
                { value: "full_day", label: "Full Day", desc: "Maximum utilization, lower average yield" },
              ].map(opt => (
                <button key={opt.value} onClick={() => updateDecision("schedule_optimization", opt.value)} className={`w-full p-3 text-left border-2 rounded-lg transition-colors ${decision.schedule_optimization === opt.value ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`schedule-${opt.value}`}>
                  <span className="font-medium text-sm">{opt.label}</span>
                  <p className="text-xs text-slate-500">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex justify-between mb-1"><Label className="text-xs">{t("decisions.utilizationTarget")}</Label><span className="font-mono text-sm text-sky-600 font-semibold">{decision.aircraft_utilization_target || 10}h/day</span></div>
            <Slider value={[decision.aircraft_utilization_target || 10]} onValueChange={([v]) => updateDecision("aircraft_utilization_target", v)} min={8} max={14} step={1} className="mt-2" data-testid="utilization-slider" />
            <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>8h (low stress)</span><span>11-12h (industry avg)</span><span>14h (max)</span></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">{t("decisions.turnaroundTime")}</Label>
              <Select value={decision.turnaround_time || "standard"} onValueChange={v => updateDecision("turnaround_time", v)}>
                <SelectTrigger className="mt-1" data-testid="turnaround-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fast">{t("decisions.turnaroundFast")}</SelectItem>
                  <SelectItem value="standard">{t("decisions.turnaroundStandard")}</SelectItem>
                  <SelectItem value="slow">{t("decisions.turnaroundSlow")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t("decisions.slotTiming")}</Label>
              <Select value={decision.slot_timing || "business_hours"} onValueChange={v => updateDecision("slot_timing", v)}>
                <SelectTrigger className="mt-1" data-testid="slot-timing-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="early_morning">{t("decisions.slotEarlyMorning")}</SelectItem>
                  <SelectItem value="business_hours">{t("decisions.slotBusinessHours")}</SelectItem>
                  <SelectItem value="evening">{t("decisions.slotEvening")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {team?.has_codeshare && (
            <div>
              <Label className="text-xs">{t("decisions.codeshareRoutes")}</Label>
              <p className="text-xs text-slate-500 mt-1">Select which routes to codeshare with partner airline (boosts load factor on thin routes)</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {(team?.active_routes || []).map(routeId => {
                  const isCodeshare = (decision.codeshare_routes || []).includes(routeId);
                  return (
                    <button key={routeId} onClick={() => {
                      const routes = isCodeshare ? (decision.codeshare_routes || []).filter(r => r !== routeId) : [...(decision.codeshare_routes || []), routeId];
                      updateDecision("codeshare_routes", routes);
                    }} className={`px-3 py-1.5 rounded border text-xs font-medium ${isCodeshare ? "bg-sky-100 border-sky-300 text-sky-700" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                      {routeId}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
